// AGC: LotesList - Shows lots (talhões/lotes) for the current farm
// AGC: Visual Refresh Design System - Clean, data-centric layout
// AGC: NOW WITH: Premium LOD card design with full-bleed crop images
import React, { useState, useMemo, useEffect } from 'react'
import { 
  Search, 
  Map, 
  ChevronDown, 
  Archive, 
  RotateCcw, 
  Loader2, 
  Pencil, 
  Trash2, 
  X, 
  Check, 
  Merge, 
  Plus,
  Sprout,
  Compass,
  // AGC: Crop icons - minimalist single-color
  Wheat,
  Leaf,
  Flower2,
  Circle,
  Bean,
  Coffee,
  Apple,
  Grape,
  TreeDeciduous,
} from 'lucide-react'
import { Card, Badge, EmptyState, Input } from '../shared'
import { BottomNav, FloatingActionButton } from '../shared'
import { getCropDisplayInfo, type Plot, type Screen } from '../../types'
import { useLotCurrentWeather } from '../../hooks/useLotCurrentWeather'
import { formatArea } from '../../services/polygonService'
import { movePlotToField, createField, deleteField } from '../../services/fields'
// AGC: [#7] Reuse the onboarding location pickers so "crear nuevo campo" captures real coordinates
// AGC: (address autocomplete + map point) instead of a free-text field with hardcoded coords.
import { AddressAutocomplete, type AddressResult } from '../wizard/AddressAutocomplete'
import { MapLocationPicker } from '../wizard/MapLocationPicker'
import { useLotes, useFarms, useDeleteConfirm } from '../../context'
import { useAuthContext } from '../../context/AuthContext'
import { useTranslation } from '../../hooks/useTranslation'
import { useHasPermission } from '../../hooks/useRBAC'
import { useIsDesktopLayout } from '../../hooks/useIsDesktopLayout'
import { calculateLotProgress } from '../../utils/lotProgress'
import { LoteCardPremium } from './LoteCardPremium'
import { getOrFetchCropImage } from '../../services/cropImageCacheService'
import { getCropImageUrl } from '../../config/localCropImages'
import toast from 'react-hot-toast'

const ALL_FARMS_ID = '__ALL_FARMS__'

// AGC: Crop icon mapping - minimalist single-color Lucide icons
// AGC: Style matches recent activities on dashboard (rounded bg + icon)
const cropIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  // AGC: Grains
  soja: Bean,
  milho: Wheat,
  trigo: Wheat,
  arroz: Leaf,
  sorgo: Wheat,
  aveia: Wheat,
  cevada: Wheat,
  centeio: Wheat,
  // AGC: Fiber crops
  algodao: Flower2,
  // AGC: Coffee and fruits
  cafe: Coffee,
  laranja: Circle,
  limao: Circle,
  uva: Grape,
  maca: Apple,
  // AGC: Other crops
  cana: Leaf,
  feijao: Bean,
  amendoim: Bean,
  girassol: Flower2,
  canola: Flower2,
  // AGC: Trees and pasture
  eucalipto: TreeDeciduous,
  pinus: TreeDeciduous,
  pastagem: Leaf,
  // AGC: Default
  default: Sprout,
}

// AGC: Get crop icon component
const getCropIcon = (cropType: string | null | undefined): React.ComponentType<{ className?: string }> => {
  if (!cropType) return cropIconMap.default
  const normalizedType = cropType.toLowerCase().replace(/[^a-z]/g, '')
  return cropIconMap[normalizedType] || cropIconMap.default
}

interface LotesListProps {
  onNavigate: (screen: Screen, data?: { plotId?: string; fieldId?: string; farmId?: string }) => void
}

/**
 * AGC: LotesList - Lot listing with Visual Refresh design
 * AGC: Features:
 *   - Clean card-based layout
 *   - Subtle shadows and borders
 *   - Monochrome icons
 *   - Staggered animations
 */
export const LotesList: React.FC<LotesListProps> = ({ onNavigate }) => {
  const { plots, archivedPlots, showArchived, setShowArchived, unarchivePlot } = useLotes()
  const { currentFarm, farms, isLoading: isLoadingFarms, setCurrentFarmById, currentFarmDisplayName, updateFarm, deleteFarm, refreshFarms } = useFarms()
  const { getAuthToken } = useAuthContext()
  const canCreateField = useHasPermission('canCreateField')
  const canDeleteField = useHasPermission('canDeleteField')
  const canEditField = useHasPermission('canEditField')
  const { t } = useTranslation()
  // AGC: Desktop layout — switch lots list from a vertical stack to a multi-column grid.
  // AGC: Hook returns false on Capacitor native, so mobile/APK is byte-for-byte unchanged.
  const isDesktop = useIsDesktopLayout()
  const { confirmBeforeDelete, requestConfirmDelete } = useDeleteConfirm()
  const [searchQuery, setSearchQuery] = useState('')
  const [showFarmSelector, setShowFarmSelector] = useState(false)
  const [unarchivingId, setUnarchivingId] = useState<string | null>(null)
  const [showSearchModal, setShowSearchModal] = useState(false)
  
  // AGC: Farm management state
  const [showAllFarms, setShowAllFarms] = useState(true)
  const [editingFarmId, setEditingFarmId] = useState<string | null>(null)
  const [editingFarmName, setEditingFarmName] = useState('')
  const [deletingFarmId, setDeletingFarmId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSavingFarm, setIsSavingFarm] = useState(false)
  
  // AGC: Merge state
  const [mergingIntoFarmId, setMergingIntoFarmId] = useState<string | null>(null)
  const [selectedFarmsToMerge, setSelectedFarmsToMerge] = useState<Set<string>>(new Set())
  const [isMerging, setIsMerging] = useState(false)
  
  // AGC: Farm creation modal state
  const [showCreateFarmModal, setShowCreateFarmModal] = useState(false)
  const [newFarmName, setNewFarmName] = useState('')
  const [newFarmLocation, setNewFarmLocation] = useState('')
  // AGC: [#7] Real coordinates captured from address autocomplete or the map picker (null = none yet).
  const [newFarmCoords, setNewFarmCoords] = useState<[number, number] | null>(null)
  const [showFarmMapPicker, setShowFarmMapPicker] = useState(false)
  const [isCreatingFarm, setIsCreatingFarm] = useState(false)

  // AGC: Get lots filtered by farm
  const farmLots: Plot[] = useMemo(() => {
    if (showAllFarms) {
      return plots
    }
    
    if (!currentFarm) {
      return []
    }
    
    const matchingPlots = plots.filter(plot => plot.fieldId === currentFarm.id)
    const orphanedPlots = plots.filter(plot => 
      plot.fieldId && 
      (plot.fieldId.startsWith('field-') || plot.fieldId.startsWith('mock-'))
    )
    
    let result = matchingPlots
    if (farms.length === 1 && orphanedPlots.length > 0 && matchingPlots.length === 0) {
      result = orphanedPlots
    }
    
    return result
  }, [plots, currentFarm, farms.length, showAllFarms])

  // AGC: Sort lots by createdAt descending (newest first) then filter by search query
  const sortedFarmLots = useMemo(() => {
    return [...farmLots].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return bTime - aTime
    })
  }, [farmLots])

  const filteredLots = useMemo(() => {
    if (!searchQuery.trim()) return sortedFarmLots
    
    const query = searchQuery.toLowerCase()
    return sortedFarmLots.filter(lot => 
      lot.name.toLowerCase().includes(query)
    )
  }, [sortedFarmLots, searchQuery])

  // AGC: Archived lots
  const farmArchivedLots: Plot[] = useMemo(() => {
    if (showAllFarms) {
      return archivedPlots
    }
    if (!currentFarm) return []
    return archivedPlots.filter(plot => plot.fieldId === currentFarm.id)
  }, [archivedPlots, currentFarm, showAllFarms])

  // AGC: Sort archived lots by createdAt descending (newest first)
  const sortedArchivedLots = useMemo(() => {
    return [...farmArchivedLots].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0
      return bTime - aTime
    })
  }, [farmArchivedLots])

  const filteredArchivedLots = useMemo(() => {
    if (!searchQuery.trim()) return sortedArchivedLots
    
    const query = searchQuery.toLowerCase()
    return sortedArchivedLots.filter(lot => 
      lot.name.toLowerCase().includes(query)
    )
  }, [sortedArchivedLots, searchQuery])

  // AGC: Handle unarchive
  const handleUnarchive = async (lotId: string, fieldId: string) => {
    setUnarchivingId(lotId)
    try {
      await unarchivePlot(lotId, fieldId)
    } catch (error) {
      console.error('AGC: Error unarchiving lot:', error)
      alert(t('lotPage.unarchiveError'))
    } finally {
      setUnarchivingId(null)
    }
  }

  // AGC: Handle farm selection
  const handleFarmSelect = (farmId: string) => {
    if (farmId === ALL_FARMS_ID) {
      setShowAllFarms(true)
      setShowFarmSelector(false)
    } else {
      setShowAllFarms(false)
      setCurrentFarmById(farmId)
      setShowFarmSelector(false)
    }
  }

  // AGC: Handle map click
  const handleMapClick = () => {
    if (showAllFarms && farms.length > 0) {
      onNavigate('farm-lots-map', { farmId: farms[0].id })
    } else if (currentFarm) {
      onNavigate('farm-lots-map', { farmId: currentFarm.id })
    }
  }
  
  // AGC: Farm management handlers
  const handleStartEditFarm = (farmId: string, farmName: string) => {
    setEditingFarmId(farmId)
    setEditingFarmName(farmName)
  }
  
  const handleCancelEdit = () => {
    setEditingFarmId(null)
    setEditingFarmName('')
  }
  
  const handleSaveFarmName = async (farmId: string) => {
    if (!editingFarmName.trim()) return
    
    setIsSavingFarm(true)
    try {
      await updateFarm(farmId, { name: editingFarmName.trim() })
      setEditingFarmId(null)
      setEditingFarmName('')
    } catch (error) {
      console.error('AGC: Error updating farm name:', error)
      alert(t('fields.farmUpdateError') || 'Error updating farm name')
    } finally {
      setIsSavingFarm(false)
    }
  }
  
  const handleDeleteFarm = async (farmId: string) => {
    const plotsInFarm = plots.filter(p => p.fieldId === farmId).length
    if (plotsInFarm > 0) {
      alert(t('fields.cannotDeleteFarmWithPlots') || 'Cannot delete farm with lots')
      return
    }
    
    setIsDeleting(true)
    try {
      await deleteFarm(farmId)
      setDeletingFarmId(null)
      if (currentFarm?.id === farmId) {
        setShowAllFarms(true)
      }
    } catch (error) {
      console.error('AGC: Error deleting farm:', error)
      alert(t('fields.farmDeleteError') || 'Error deleting farm')
    } finally {
      setIsDeleting(false)
    }
  }
  
  const handleStartMerge = (farmId: string) => {
    setMergingIntoFarmId(farmId)
    setSelectedFarmsToMerge(new Set())
  }
  
  const handleCancelMerge = () => {
    setMergingIntoFarmId(null)
    setSelectedFarmsToMerge(new Set())
  }
  
  const handleToggleFarmForMerge = (farmId: string) => {
    setSelectedFarmsToMerge(prev => {
      const newSet = new Set(prev)
      if (newSet.has(farmId)) {
        newSet.delete(farmId)
      } else {
        newSet.add(farmId)
      }
      return newSet
    })
  }
  
  const handleConfirmMerge = async () => {
    if (!mergingIntoFarmId || selectedFarmsToMerge.size === 0) return
    
    const targetFarm = farms.find(f => f.id === mergingIntoFarmId)
    if (!targetFarm) return
    
    const plotsToMove = plots.filter(p => selectedFarmsToMerge.has(p.fieldId))
    
    if (plotsToMove.length === 0) {
      alert(t('fields.noPlotsTomMerge') || 'No lots to merge')
      handleCancelMerge()
      return
    }
    
    const confirmMessage = t('fields.confirmMerge', { 
      count: plotsToMove.length, 
      farmName: targetFarm.name 
    }) || `Move ${plotsToMove.length} lot(s) to "${targetFarm.name}"?`
    
    if (!confirm(confirmMessage)) return
    
    setIsMerging(true)
    try {
      const token = await getAuthToken()
      
      // AGC: Step 1 - Move all plots to the target farm
      for (const plot of plotsToMove) {
        await movePlotToField(plot.fieldId, plot.id, mergingIntoFarmId, token)
      }
      
      // AGC: Step 2 - Delete the now-empty source farms
      // AGC: These farms had all their lots moved to the target, so they should be removed
      const sourceFarmIds = Array.from(selectedFarmsToMerge)
      for (const farmId of sourceFarmIds) {
        try {
          await deleteField(farmId, token)
          console.log('🗑️ AGC: Deleted empty source farm:', farmId)
        } catch (deleteError) {
          // AGC: Log but don't fail the whole merge if a delete fails
          console.warn('⚠️ AGC: Failed to delete source farm:', farmId, deleteError)
        }
      }
      
      // AGC: Step 3 - Clear ALL cached data to force fresh load from Firestore
      const keysToScan = Object.keys(localStorage)
      for (const key of keysToScan) {
        if (
          key.startsWith('droidfarm_plots_') ||
          key.startsWith('droidfarm_plots_sync_timestamp_') ||
          key.startsWith('droidfarm_plots_pending_changes_') ||
          key.startsWith('droidfarm_farms_cache_')
        ) {
          localStorage.removeItem(key)
          console.log('🧹 AGC: Cleared cache key:', key)
        }
      }
      
      // AGC: Step 4 - Set current farm to the merge target
      const userId = localStorage.getItem('droidfarm_userId')
      if (userId) {
        localStorage.setItem(`droidfarm_currentFarm_${userId}`, mergingIntoFarmId)
      }
      
      // AGC: Show success message before reload
      toast.success(
        t('fields.mergeSuccessWithDelete', { count: plotsToMove.length, deleted: sourceFarmIds.length }) 
        || `${plotsToMove.length} lot(s) merged and ${sourceFarmIds.length} empty farm(s) deleted!`
      )
      
      // AGC: Small delay to show toast, then force full reload
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error) {
      console.error('AGC: Error merging farms:', error)
      alert(t('fields.mergeError') || 'Error merging farms')
    } finally {
      setIsMerging(false)
    }
  }
  
  // AGC: Handle create new farm
  const handleCreateFarm = async () => {
    if (!newFarmName.trim()) {
      toast.error(t('fields.farmNameRequired') || 'Farm name is required')
      return
    }
    
    setIsCreatingFarm(true)
    try {
      const token = await getAuthToken()
      const farmData = {
        name: newFarmName.trim(),
        location: newFarmLocation.trim() || '',
        totalArea: 0,
        plots: [],
        // AGC: [#7] Use the coordinates captured from the address/map picker; fall back to BR center.
        coordinates: (newFarmCoords || [-15.7975, -47.8919]) as [number, number],
      }
      
      const newFarm = await createField(farmData, token)
      
      toast.success(t('fields.farmCreated') || 'Farm created successfully!')
      
      // AGC: Reset modal state
      setShowCreateFarmModal(false)
      setNewFarmName('')
      setNewFarmLocation('')
      setNewFarmCoords(null)
      setShowFarmSelector(false)
      
      // AGC: Refresh farms and select the new one
      await refreshFarms()
      setShowAllFarms(false)
      setCurrentFarmById(newFarm.id)
      
    } catch (error) {
      console.error('AGC: Error creating farm:', error)
      toast.error(t('fields.farmCreateError') || 'Error creating farm')
    } finally {
      setIsCreatingFarm(false)
    }
  }
  
  const headerDisplayName = showAllFarms 
    ? (t('fields.allFarms') || 'All Farms')
    : (currentFarmDisplayName || t('fields.loadingFarms'))

  return (
    <div className="min-h-screen bg-[#fafbfc] dark:bg-gray-900 pb-20 isolate">
      {/* AGC: `isolate` + `z-30` keep this header above lot cards whose `stagger-children` fadeInUp animation leaves a transform and thus creates a stacking context per card. */}
      <header
        className="px-4 pt-4 pb-4 sticky top-0 z-30 rounded-b-2xl overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0a3d33 0%, #0d4a3e 50%, #1a6b5a 100%)' }}
      >
        {/* AGC: Decorative background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
          />
          <div 
            className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full opacity-15"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }}
          />
        </div>
        
        {/* AGC: Farm Name Row */}
        <div className="flex items-center justify-between mb-4 relative z-10">
          <div className="flex items-center gap-2 flex-1">
            {/* AGC: Sprout icon like weather has weather icon */}
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <Sprout className="w-5 h-5 text-white" />
            </div>
            <button
              onClick={() => setShowFarmSelector(!showFarmSelector)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors flex-1 text-left"
            >
              <h1 className="text-lg font-semibold text-white flex-1 truncate">
                {headerDisplayName}
              </h1>
              <ChevronDown className={`w-4 h-4 text-white/70 transition-transform ${showFarmSelector ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* AGC: Farm Selector Dropdown - Green themed to match header */}
        {showFarmSelector && (
          <Card 
            variant="elevated" 
            padding="none" 
            className="mb-4 max-h-80 overflow-y-auto relative z-20 border-2 border-white/20"
            style={{ 
              background: 'linear-gradient(135deg, rgba(10,61,51,0.95) 0%, rgba(13,74,62,0.95) 50%, rgba(26,107,90,0.95) 100%)',
              backdropFilter: 'blur(12px)'
            }}
          >
            {/* AGC: All Farms Option */}
            <button
              onClick={() => handleFarmSelect(ALL_FARMS_ID)}
              className={`w-full px-4 py-3 text-left hover:bg-white/10 transition-colors border-b border-white/10 ${
                showAllFarms ? 'bg-white/20 border-l-4 border-l-white' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-white">
                  {t('fields.allFarms') || 'All Farms'}
                </span>
                <span className="px-2 py-0.5 rounded-md bg-white/20 text-white text-xs font-medium">
                  {plots.length} {plots.length === 1 ? 'lot' : 'lots'}
                </span>
              </div>
            </button>
            
            {/* AGC: Individual Farms */}
            {farms.map((farm) => {
              const plotsInFarm = plots.filter(p => p.fieldId === farm.id).length
              const isEditing = editingFarmId === farm.id
              const isConfirmingDelete = deletingFarmId === farm.id
              const canDelete = plotsInFarm === 0
              const isSelected = !showAllFarms && currentFarm?.id === farm.id
              
              return (
                <div
                  key={farm.id}
                  className={`px-4 py-3 border-b border-white/10 last:border-b-0 ${
                    isSelected ? 'bg-white/20 border-l-4 border-l-white' : ''
                  }`}
                >
                  {isEditing ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editingFarmName}
                        onChange={(e) => setEditingFarmName(e.target.value)}
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                        placeholder={t('fields.farmName') || 'Farm name'}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveFarmName(farm.id)
                          if (e.key === 'Escape') handleCancelEdit()
                        }}
                      />
                      <button
                        onClick={() => handleSaveFarmName(farm.id)}
                        disabled={isSavingFarm || !editingFarmName.trim()}
                        className="p-1.5 rounded-lg bg-navy-700 text-white hover:bg-navy-600 disabled:opacity-50"
                      >
                        {isSavingFarm ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="p-1.5 rounded-lg bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : isConfirmingDelete ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-red-600 dark:text-red-400">
                        {t('fields.confirmDeleteFarm') || 'Confirm deletion?'}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDeleteFarm(farm.id)}
                          disabled={isDeleting}
                          className="px-3 py-1 text-xs font-medium bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                        >
                          {isDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : (t('common.delete') || 'Delete')}
                        </button>
                        <button
                          onClick={() => setDeletingFarmId(null)}
                          className="px-3 py-1 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                        >
                          {t('common.cancel') || 'Cancel'}
                        </button>
                      </div>
                    </div>
                  ) : mergingIntoFarmId !== null ? (
                    <div className="flex items-center gap-2">
                      {farm.id === mergingIntoFarmId ? (
                        <div className="flex-1 flex items-center gap-2">
                          <div className="w-6 h-6 rounded bg-navy-700 flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-navy-700 dark:text-navy-400">{farm.name}</div>
                            <div className="text-xs text-navy-600/70 dark:text-navy-400/70">
                              {t('fields.mergeTarget') || 'Merge destination'}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleConfirmMerge}
                              disabled={selectedFarmsToMerge.size === 0 || isMerging}
                              className="px-3 py-1.5 text-xs font-medium bg-navy-700 text-white rounded-lg hover:bg-navy-600 disabled:opacity-50 flex items-center gap-1"
                            >
                              {isMerging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Merge className="w-3 h-3" />}
                              {t('fields.mergeFarms') || 'Merge'}
                            </button>
                            <button
                              onClick={handleCancelMerge}
                              className="px-3 py-1.5 text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500"
                            >
                              {t('common.cancel') || 'Cancel'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleToggleFarmForMerge(farm.id)}
                          className="flex-1 flex items-center gap-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 -mx-2 px-2 py-1 rounded-lg transition-colors"
                        >
                          <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                            selectedFarmsToMerge.has(farm.id) 
                              ? 'bg-navy-700 border-navy-700' 
                              : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {selectedFarmsToMerge.has(farm.id) && <Check className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className={`font-medium ${
                                selectedFarmsToMerge.has(farm.id) 
                                  ? 'text-navy-700 dark:text-navy-400' 
                                  : 'text-gray-900 dark:text-gray-50'
                              }`}>{farm.name}</span>
                              {plotsInFarm > 0 && (
                                <Badge variant={selectedFarmsToMerge.has(farm.id) ? 'accent' : 'default'}>
                                  {plotsInFarm} {plotsInFarm === 1 ? 'lot' : 'lots'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleFarmSelect(farm.id)}
                        className="flex-1 text-left hover:bg-white/5 -mx-2 px-2 py-1 rounded-lg transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-white">{farm.name}</span>
                          {plotsInFarm > 0 && (
                            <span className="px-2 py-0.5 rounded-md bg-white/20 text-white text-xs font-medium">
                              {plotsInFarm} {plotsInFarm === 1 ? 'lot' : 'lots'}
                            </span>
                          )}
                        </div>
                        {farm.location && <div className="text-sm text-white/70">{farm.location}</div>}
                      </button>
                      
                      <button
                        onClick={() => handleStartMerge(farm.id)}
                        className="p-1.5 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                        title={t('fields.mergeFarms') || 'Merge farms'}
                      >
                        <Merge className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => handleStartEditFarm(farm.id, farm.name)}
                        className="p-1.5 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                        title={t('common.edit') || 'Edit'}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => {
                          if (!canDelete) return
                          if (confirmBeforeDelete) {
                            requestConfirmDelete({ onConfirm: () => handleDeleteFarm(farm.id) })
                          } else {
                            setDeletingFarmId(farm.id)
                          }
                        }}
                        disabled={!canDelete}
                        className={`p-1.5 rounded-lg transition-colors ${
                          canDelete 
                            ? 'text-white/70 hover:bg-red-500/20 hover:text-red-400' 
                            : 'text-white/30 cursor-not-allowed'
                        }`}
                        title={canDelete 
                          ? (t('common.delete') || 'Delete')
                          : (t('fields.cannotDeleteFarmWithPlots') || 'Cannot delete farm with lots')
                        }
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
            
            {/* AGC: Create New Farm Button */}
            <button
              onClick={() => {
                setShowFarmSelector(false)
                setShowCreateFarmModal(true)
              }}
              className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors border-t border-white/20 flex items-center gap-2"
            >
              <div className="w-6 h-6 rounded bg-white/20 flex items-center justify-center">
                <Plus className="w-4 h-4 text-white" />
              </div>
              <span className="font-medium text-white">
                {t('fields.createNewFarm') || 'Create New Farm'}
              </span>
            </button>
          </Card>
        )}
        
        {/* AGC: Compact Header with Search and Map Icons - styled for gradient background */}
        <div className="flex items-center gap-2 relative z-10">
          {/* AGC: Active/Archived Toggle - compact chips */}
          <div className="flex items-center bg-white/10 backdrop-blur-sm rounded-lg p-0.5 gap-0.5">
            <button
              onClick={() => setShowArchived(false)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                !showArchived 
                  ? 'bg-white/30 text-white shadow-sm' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              {t('fields.activeLots').split(' ')[0]} ({filteredLots.length})
            </button>
            <button
              onClick={() => setShowArchived(true)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${
                showArchived 
                  ? 'bg-white/30 text-white shadow-sm' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <Archive className="w-3 h-3" />
              <span className="hidden sm:inline">{t('fields.archivedLots').split(' ')[0]}</span> ({filteredArchivedLots.length})
            </button>
          </div>
          
          {/* AGC: Map and Search Icons - same square button style */}
          <div className="flex items-center gap-1.5 ml-auto">
            <button
              onClick={handleMapClick}
              className="p-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-colors"
              title={t('fields.viewMap')}
            >
              <Map className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowSearchModal(true)}
              className="p-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-colors"
              title={t('fields.searchWithFilters') || 'Search lots'}
            >
              <Search className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* AGC: Search Modal - Full screen with filters */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-start justify-center pt-16 px-4">
          <Card className="w-full max-w-lg bg-white dark:bg-slate-800 shadow-2xl">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                  {t('fields.searchLots')}
                </h3>
                <button
                  onClick={() => setShowSearchModal(false)}
                  className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* AGC: Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('fields.searchLots')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                />
              </div>
            </div>
            
            {/* AGC: Search Results Preview */}
            <div className="p-4 max-h-96 overflow-y-auto">
              {searchQuery.trim() ? (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {filteredLots.length} {filteredLots.length === 1 ? t('fields.lotFound') : t('fields.lotsFound')}
                  </p>
                  {filteredLots.slice(0, 5).map(lot => (
                    <button
                      key={lot.id}
                      onClick={() => {
                        setShowSearchModal(false)
                        onNavigate('lote', { plotId: lot.id, fieldId: lot.fieldId })
                      }}
                      className="w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 mb-2 transition-colors"
                    >
                      <p className="font-medium text-gray-900 dark:text-gray-50">{lot.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {getCropDisplayInfo(lot.cropType).label} • {formatArea(lot.area)}
                      </p>
                    </button>
                  ))}
                  {filteredLots.length > 5 && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2">
                      +{filteredLots.length - 5} {t('common.more')}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                  {t('fields.typeToSearch') || 'Type to search lots...'}
                </p>
              )}
            </div>
            
            {/* AGC: Actions */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowSearchModal(false)
                  setSearchQuery('')
                }}
                className="w-full px-4 py-2 bg-navy-700 text-white rounded-lg hover:bg-navy-600 font-medium transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* AGC: Create Farm Modal */}
      {showCreateFarmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-center justify-center px-4">
          <Card className="w-full max-w-md bg-white dark:bg-slate-800 shadow-2xl">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <Sprout className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  {t('fields.createNewFarm') || 'Create New Farm'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateFarmModal(false)
                    setNewFarmName('')
                    setNewFarmLocation('')
                  }}
                  disabled={isCreatingFarm}
                  className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-600 transition-colors disabled:opacity-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              {/* AGC: Farm Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fields.farmName') || 'Farm Name'} *
                </label>
                <input
                  type="text"
                  value={newFarmName}
                  onChange={(e) => setNewFarmName(e.target.value)}
                  placeholder={t('fields.farmNamePlaceholder') || 'e.g., Santa Maria Farm'}
                  className="w-full px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                  autoFocus
                  disabled={isCreatingFarm}
                />
              </div>
              
              {/* AGC: [#7] Location — same options as onboarding: address autocomplete (geocoded → real
                  coordinates) + map point picker. Replaces the free-text field that hardcoded BR coords. */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('fields.farmLocation') || 'Location'} ({t('common.optional') || 'optional'})
                </label>
                <AddressAutocomplete
                  countryCode=""
                  placeholder={t('fields.farmLocationPlaceholder') || 'Buscar dirección...'}
                  onSelect={(r: AddressResult) => {
                    setNewFarmCoords([parseFloat(r.lat), parseFloat(r.lon)])
                    const city = r.address.city || r.address.town || r.address.village || ''
                    const state = r.address.state || ''
                    setNewFarmLocation((city || state) ? `${city}${city && state ? ', ' : ''}${state}` : r.display_name)
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowFarmMapPicker(true)}
                  disabled={isCreatingFarm}
                  className="mt-2 text-sm text-emerald-700 dark:text-emerald-400 hover:underline disabled:opacity-50"
                >
                  📍 {t('fields.pickOnMap') || 'Elegir en el mapa'}
                </button>
                {newFarmCoords && (
                  <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-200">
                    {newFarmLocation || `${newFarmCoords[0].toFixed(4)}, ${newFarmCoords[1].toFixed(4)}`}
                  </div>
                )}
              </div>
            </div>
            
            {/* AGC: Actions */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowCreateFarmModal(false)
                  setNewFarmName('')
                  setNewFarmLocation('')
                  setNewFarmCoords(null)
                }}
                disabled={isCreatingFarm}
                className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 font-medium transition-colors disabled:opacity-50"
              >
                {t('common.cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleCreateFarm}
                disabled={isCreatingFarm || !newFarmName.trim()}
                className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-600 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isCreatingFarm ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.creating') || 'Creating...'}
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    {t('common.create') || 'Create'}
                  </>
                )}
              </button>
            </div>
            {/* AGC: [#7] Map point picker overlay — captures real coordinates for the new campo. */}
            {showFarmMapPicker && (
              <MapLocationPicker
                countryCode=""
                initialCenter={newFarmCoords || undefined}
                onLocationSelect={(loc: { lat: number; lng: number; address: string }) => {
                  setNewFarmCoords([loc.lat, loc.lng])
                  setNewFarmLocation(loc.address)
                  setShowFarmMapPicker(false)
                }}
                onClose={() => setShowFarmMapPicker(false)}
              />
            )}
          </Card>
        </div>
      )}

      {/* AGC: Lots List — vertical stack on mobile, auto-fit grid (2-4+ cards/row) on desktop */}
      <div
        className={
          isDesktop
            ? 'px-4 py-4 grid gap-4 stagger-children grid-cols-[repeat(auto-fill,minmax(300px,1fr))]'
            : 'px-4 py-4 space-y-3 stagger-children'
        }
      >
        {isLoadingFarms ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-navy-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">{t('fields.loadingLots')}</p>
          </div>
        ) : (!currentFarm && !showAllFarms) ? (
          <EmptyState
            icon={<Sprout />}
            title={t('fields.noLots')}
            description={t('fields.addFirstLot')}
          />
        ) : showArchived ? (
          filteredArchivedLots.length === 0 ? (
            <EmptyState
              icon={<Archive />}
              title={t('fields.noArchivedLots')}
              description={t('fields.noArchivedLotsDescription')}
            />
          ) : (
            filteredArchivedLots.map((lot) => (
              <ArchivedLoteCard
                key={lot.id}
                lot={lot}
                onUnarchive={() => handleUnarchive(lot.id, lot.fieldId)}
                isUnarchiving={unarchivingId === lot.id}
              />
            ))
          )
        ) : (
          filteredLots.length === 0 ? (
            <EmptyState
              icon={<Sprout />}
              title={searchQuery ? t('common.noResults') : t('fields.noLotsInFarm')}
              description={!searchQuery && plots.length === 0 ? t('fields.addFirstLot') : undefined}
              action={!searchQuery && plots.length === 0 ? {
                label: t('fields.newLot') || 'New Lot',
                onClick: () => onNavigate('novo-lote'),
                icon: <Plus className="w-4 h-4" />,
              } : undefined}
            />
          ) : (
            filteredLots.map((lot) => (
              <LoteCardPremiumWrapper
                key={lot.id}
                lot={lot}
                onClick={() => onNavigate('lot-page', { plotId: lot.id })}
                farmCoords={currentFarm?.coordinates as [number, number] | undefined}
              />
            ))
          )
        )}
      </div>

      {/* AGC: FAB — only show if user has permission to create fields */}
      {canCreateField && (currentFarm || (showAllFarms && farms.length > 0)) && (
        <FloatingActionButton onClick={() => onNavigate('novo-lote')} />
      )}

      <BottomNav 
        active="fields" 
        onNavigate={(tab) => {
          if (tab === 'dashboard') onNavigate('dashboard')
          else if (tab === 'tasks') onNavigate('tasks')
          else if (tab === 'weather') onNavigate('weather')
        }} 
      />
    </div>
  )
}

/**
 * AGC: LoteCardPremium Wrapper with image caching
 * AGC: Fetches crop image from cache or loads it
 */
interface LoteCardPremiumWrapperProps {
  lot: Plot
  onClick: () => void
  farmCoords?: [number, number]
}

const LoteCardPremiumWrapper: React.FC<LoteCardPremiumWrapperProps> = ({ lot, onClick, farmCoords }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [isImageLoading, setIsImageLoading] = useState(true)
  const cropDisplay = getCropDisplayInfo(lot.cropType)
  const weather = useLotCurrentWeather(lot, farmCoords)

  // AGC: Load crop image with priority: Admin upload > Local test image > null
  useEffect(() => {
    let mounted = true
    
    async function loadImage() {
      // AGC: Priority 1 - Try admin-uploaded image (Firebase or manual URL)
      if (cropDisplay.imageUrl) {
        try {
          const cachedUrl = await getOrFetchCropImage(
            lot.cropType,
            cropDisplay.imageUrl,
            cropDisplay.imageUpdatedAt
          )
          
          if (mounted) {
            setImageUrl(cachedUrl)
            setIsImageLoading(false)
          }
          return // AGC: Success, stop here
        } catch (error) {
          console.warn(`⚠️ AGC: Failed to load admin image for ${lot.cropType}:`, error)
          // AGC: Fall through to local image
        }
      }
      
      // AGC: Priority 2 - Try local testing image (from /public/crop-images/)
      const localImageUrl = getCropImageUrl(lot.cropType, null)
      if (localImageUrl) {
        console.log(`🖼️ AGC: Using local test image for ${lot.cropType}: ${localImageUrl}`)
        if (mounted) {
          setImageUrl(localImageUrl)
        }
      }
      
      // AGC: Done loading (whether we found an image or not)
      if (mounted) {
        setIsImageLoading(false)
      }
    }
    
    loadImage()
    
    return () => {
      mounted = false
      // AGC: Only revoke blob URLs (from cache), not public URLs
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl)
      }
    }
  }, [lot.cropType, cropDisplay.imageUrl, cropDisplay.imageUpdatedAt])
  
  return (
    <LoteCardPremium
      lot={lot}
      onClick={onClick}
      imageUrl={imageUrl}
      isImageLoading={isImageLoading}
      weather={weather}
    />
  )
}

/**
 * AGC: OLD Lot Card - Kept for reference, can be removed later
 */
interface LoteCardProps {
  lot: Plot
  onClick: () => void
}

const LoteCard: React.FC<LoteCardProps> = ({ lot, onClick }) => {
  const cropDisplay = getCropDisplayInfo(lot.cropType)
  const progress = useMemo(() => calculateLotProgress(lot), [lot])
  const { t } = useTranslation()
  const CropIcon = getCropIcon(lot.cropType)

  const statusLabels: Record<string, string> = {
    'por-semear': t('fields.status.toSow') || 'To sow',
    'semeado': t('fields.status.sown') || 'Sown',
    'em-desenvolvimento': t('fields.status.growing') || 'Growing',
    'pronto-colher': t('fields.status.readyHarvest') || 'Ready to harvest',
  }

  const statusLabel = statusLabels[lot.managementStatus] || lot.managementStatus

  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 transition-all duration-200 hover:bg-slate-200/70 dark:hover:bg-slate-700/70 hover:border-slate-300 dark:hover:border-slate-600 active:scale-[0.98]"
    >
      {/* AGC: Header with crop icon */}
      <div className="flex items-start gap-3 mb-3">
        {/* AGC: Crop icon - same style as dashboard activities */}
        <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
          <CropIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50 truncate">{lot.name}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{cropDisplay.name}</p>
        </div>
      </div>

      {/* AGC: Info Grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{t('fields.hectares') || 'Hectares'}</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-50">{formatArea(lot.area)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{t('fields.status.label') || 'Status'}</p>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{statusLabel}</p>
        </div>
      </div>

      {/* AGC: Progress Bar */}
      <div>
        <div className="flex justify-between items-center mb-1.5">
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('fields.progress') || 'Progress'}</p>
          <p className="text-xs font-semibold text-navy-600 dark:text-navy-400">{progress}%</p>
        </div>
        <div className="w-full h-1.5 bg-gray-300/50 dark:bg-gray-600 rounded-full overflow-hidden">
          <div
            className="h-full bg-navy-600 dark:bg-navy-400 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </button>
  )
}

/**
 * AGC: Archived Lot Card
 */
interface ArchivedLoteCardProps {
  lot: Plot
  onUnarchive: () => void
  isUnarchiving: boolean
}

const ArchivedLoteCard: React.FC<ArchivedLoteCardProps> = ({ lot, onUnarchive, isUnarchiving }) => {
  const { t } = useTranslation()
  const cropDisplay = getCropDisplayInfo(lot.cropType)
  const archivedDate = lot.archivedAt 
    ? new Date(lot.archivedAt).toLocaleDateString()
    : ''

  return (
    <div className="p-4 rounded-xl bg-slate-100/60 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 opacity-75">
      {/* AGC: Header with archive badge */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300">{lot.name}</h3>
            <Badge variant="warning" size="sm" icon={<Archive className="w-3 h-3" />}>
              {t('fields.archived')}
            </Badge>
          </div>
          {archivedDate && (
            <p className="text-xs text-gray-400 mt-1">
              {t('fields.archivedOn')} {archivedDate}
            </p>
          )}
        </div>
      </div>

      {/* AGC: Info Grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <p className="text-xs text-gray-500">{t('fields.hectares') || 'Hectares'}</p>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{formatArea(lot.area)}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">{t('fields.crop') || 'Crop'}</p>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{cropDisplay.name}</p>
        </div>
      </div>

      {/* AGC: Unarchive Button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onUnarchive()
        }}
        disabled={isUnarchiving}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-navy-700 text-white rounded-full hover:bg-navy-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
      >
        {isUnarchiving ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {t('common.loading')}
          </>
        ) : (
          <>
            <RotateCcw className="w-4 h-4" />
            {t('fields.unarchiveLot')}
          </>
        )}
      </button>
    </div>
  )
}

export default LotesList
